
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
// import { supabase } from "@/integrations/supabase/client"; // TEMPORARILY DISABLED
// import { Printer, toPrinter } from "@/types/printer"; // TEMPORARILY DISABLED
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, MoreHorizontal, Save, Trash2, RefreshCw } from "lucide-react";

interface ComponentType {
  id: number;
  name: string;
  table_name: string;
  description?: string;
}

interface TableColumn {
  name: string;
  type: string;
  is_nullable: boolean;
  is_identity?: boolean;
  default_value?: string | null;
}

interface ComponentEntry {
  id: number | string;
  [key: string]: any;
}

// Type for form data
type FormData = Record<string, any>;

// TEMPORARILY USING MOCK DATA
const mockComponentTypes: ComponentType[] = [
  { id: 1, name: "Hotends", table_name: "hotends", description: "Hotend components" },
  { id: 2, name: "Extruders", table_name: "extruders", description: "Extruder components" },
  { id: 3, name: "Probes", table_name: "probes", description: "Probe components" },
  { id: 4, name: "Fans", table_name: "fans", description: "Fan components" },
];

const mockTableColumns: TableColumn[] = [
  { name: "id", type: "bigint", is_nullable: false, is_identity: true },
  { name: "name", type: "text", is_nullable: false },
  { name: "description", type: "text", is_nullable: true },
  { name: "manufacturer", type: "text", is_nullable: true },
  { name: "rating", type: "real", is_nullable: true },
];

const mockEntries: ComponentEntry[] = [
  { id: 1, name: "Sample Component 1", description: "Description 1", manufacturer: "Manufacturer A", rating: 4.5 },
  { id: 2, name: "Sample Component 2", description: "Description 2", manufacturer: "Manufacturer B", rating: 4.0 },
];

const mockPrinters = [
  { id: "1", name: "Ender 3 V2", model: "Ender 3 V2" },
  { id: "2", name: "Prusa i3 MK3S+", model: "Prusa i3 MK3S+" },
];

const ComponentManager = () => {
  const navigate = useNavigate();
  const [componentTypes, setComponentTypes] = useState<ComponentType[]>(mockComponentTypes);
  const [selectedComponent, setSelectedComponent] = useState<ComponentType | null>(null);
  const [loadingComponents, setLoadingComponents] = useState(false); // Changed to false since using mock data
  const [tableColumns, setTableColumns] = useState<TableColumn[]>([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [entries, setEntries] = useState<ComponentEntry[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<ComponentEntry | null>(null);
  const [printers, setPrinters] = useState(mockPrinters);
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  const form = useForm<FormData>({
    defaultValues: {},
  });

  // TEMPORARILY DISABLED SUPABASE LOADING
  /*
  // Load component types
  useEffect(() => {
    const fetchComponentTypes = async () => {
      try {
        setLoadingComponents(true);
        const { data, error } = await supabase
          .from('printer_components')
          .select('*')
          .order('name');
          
        if (error) throw error;
        
        setComponentTypes(data || []);
      } catch (error) {
        console.error("Error fetching component types:", error);
        toast.error("Failed to load component types");
      } finally {
        setLoadingComponents(false);
      }
    };
    
    fetchComponentTypes();
  }, []);
  
  // Load printers
  useEffect(() => {
    const fetchPrinters = async () => {
      try {
        setLoadingPrinters(true);
        const { data, error } = await supabase
          .from('printers')
          .select('*')
          .order('printer');
          
        if (error) throw error;
        
        // Convert raw data to Printer objects
        const printerObjects = data?.map(printer => toPrinter(printer)) || [];
        setPrinters(printerObjects);
      } catch (error) {
        console.error("Error fetching printers:", error);
        toast.error("Failed to load printers");
      } finally {
        setLoadingPrinters(false);
      }
    };
    
    fetchPrinters();
  }, []);
  */

  // TEMPORARILY DISABLED SUPABASE TABLE FETCHING
  /*
  // Fetch table columns when component type is selected
  useEffect(() => {
    if (!selectedComponent) {
      setTableColumns([]);
      setEntries([]);
      return;
    }
    
    const fetchTableColumns = async () => {
      try {
        setLoadingColumns(true);
        
        // Fetch column information from Postgres information schema
        const { data, error } = await supabase.rpc('get_table_columns', {
          table_name: selectedComponent.table_name
        });
        
        if (error) throw error;
        
        setTableColumns(data || []);
        
        // Reset form with new fields
        form.reset({});
      } catch (error) {
        console.error("Error fetching table columns:", error);
        toast.error("Failed to load table structure");
      } finally {
        setLoadingColumns(false);
      }
    };
    
    fetchTableColumns();
    fetchEntries();
  }, [selectedComponent]);
  */

  // Use mock data when component type is selected
  useEffect(() => {
    if (!selectedComponent) {
      setTableColumns([]);
      setEntries([]);
      return;
    }
    
    // Use mock data temporarily
    setTableColumns(mockTableColumns);
    setEntries(mockEntries);
    form.reset({});
  }, [selectedComponent, form]);
  
  // TEMPORARILY DISABLED SUPABASE ENTRY FETCHING
  /*
  // Fetch entries when component type changes or after operations
  const fetchEntries = async () => {
    if (!selectedComponent) return;
    
    try {
      setLoadingEntries(true);
      // Use type assertion to tell TypeScript that this is a valid table name
      const { data, error } = await supabase
        .from(selectedComponent.table_name as any)
        .select('*')
        .order('id');
        
      if (error) throw error;
      
      // Type assertion to ensure it matches our ComponentEntry[] type
      setEntries(data as ComponentEntry[] || []);
    } catch (error) {
      console.error(`Error fetching ${selectedComponent.name} entries:`, error);
      toast.error("Failed to load component entries");
    } finally {
      setLoadingEntries(false);
    }
  };
  */

  // Mock fetch entries function
  const fetchEntries = () => {
    // Use mock data temporarily
    setEntries(mockEntries);
  };
  
  // Handle component type selection
  const handleComponentChange = (componentId: string) => {
    const component = componentTypes.find(c => c.id === parseInt(componentId));
    setSelectedComponent(component || null);
    setSelectedEntry(null);
    setIsEditing(false);
    form.reset({});
  };
  
  // Handle edit entry
  const handleEditEntry = (entry: ComponentEntry) => {
    setSelectedEntry(entry);
    setIsEditing(true);
    
    // Prepare form values
    const formValues: FormData = {};
    tableColumns.forEach(column => {
      // Skip identity columns like 'id'
      if (!column.is_identity) {
        formValues[column.name] = entry[column.name] || '';
      }
    });
    
    form.reset(formValues);
  };
  
  // TEMPORARILY DISABLED SUPABASE DELETE
  /*
  // Handle delete entry
  const handleDeleteEntry = async (entry: ComponentEntry) => {
    if (!selectedComponent) return;
    
    try {
      const { error } = await supabase
        .from(selectedComponent.table_name as any)
        .delete()
        .eq('id', entry.id);
        
      if (error) throw error;
      
      toast.success(`${selectedComponent.name} deleted successfully`);
      fetchEntries();
      
      if (selectedEntry?.id === entry.id) {
        setSelectedEntry(null);
        setIsEditing(false);
        form.reset({});
      }
    } catch (error) {
      console.error("Error deleting entry:", error);
      toast.error("Failed to delete entry");
    }
  };
  */

  // Mock delete entry function
  const handleDeleteEntry = (entry: ComponentEntry) => {
    if (!selectedComponent) return;
    
    // Remove from mock data temporarily
    setEntries(entries.filter(e => e.id !== entry.id));
    toast.success(`${selectedComponent.name} deleted successfully (temporary - not saved to database)`);
    
    if (selectedEntry?.id === entry.id) {
      setSelectedEntry(null);
      setIsEditing(false);
      form.reset({});
    }
  };
  
  // TEMPORARILY DISABLED SUPABASE FORM SUBMISSION
  /*
  // Handle form submission
  const onSubmit = async (data: any) => {
    if (!selectedComponent) return;
    
    try {
      // Remove any undefined or null values
      Object.keys(data).forEach(key => {
        if (data[key] === undefined || data[key] === null || data[key] === '') {
          delete data[key];
        }
      });
      
      if (isEditing && selectedEntry) {
        // Update existing entry
        const { error } = await supabase
          .from(selectedComponent.table_name as any)
          .update(data)
          .eq('id', selectedEntry.id);
          
        if (error) throw error;
        
        toast.success(`${selectedComponent.name} updated successfully`);
      } else {
        // Create new entry
        const { error } = await supabase
          .from(selectedComponent.table_name as any)
          .insert(data);
          
        if (error) throw error;
        
        toast.success(`New ${selectedComponent.name} added successfully`);
      }
      
      // Reset form and refresh data
      form.reset({});
      setSelectedEntry(null);
      setIsEditing(false);
      fetchEntries();
    } catch (error: any) {
      console.error("Error saving entry:", error);
      toast.error(`Failed to save: ${error.message || 'Unknown error'}`);
    }
  };
  */

  // Mock form submission function
  const onSubmit = (data: FormData) => {
    if (!selectedComponent) return;
    
    // Remove any undefined or null values
    Object.keys(data).forEach(key => {
      if (data[key] === undefined || data[key] === null || data[key] === '') {
        delete data[key];
      }
    });
    
    if (isEditing && selectedEntry) {
      // Update existing entry in mock data
      setEntries(entries.map(e => e.id === selectedEntry.id ? { ...e, ...data } : e));
      toast.success(`${selectedComponent.name} updated successfully (temporary - not saved to database)`);
    } else {
      // Create new entry in mock data
      const newEntry = { id: Date.now(), ...data };
      setEntries([...entries, newEntry]);
      toast.success(`New ${selectedComponent.name} added successfully (temporary - not saved to database)`);
    }
    
    // Reset form and refresh data
    form.reset({});
    setSelectedEntry(null);
    setIsEditing(false);
  };
  
  // Generate form fields based on table columns
  const renderFormFields = () => {
    if (loadingColumns) {
      return <div className="flex justify-center p-4"><Loader2 className="animate-spin" /></div>;
    }
    
    return tableColumns.map(column => {
      // Skip identity columns like 'id'
      if (column.is_identity) return null;
      
      // Determine field type based on column type
      let fieldType = "text";
      if (column.type.includes('int')) fieldType = "number";
      if (column.type.includes('bool')) fieldType = "checkbox";
      
      // Special case for printer selection
      if (column.name === 'printer_id' || column.name === 'printer') {
        return (
          <FormField
            key={column.name}
            control={form.control}
            name={column.name}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Printer</FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value ? String(field.value) : undefined}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a printer" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {printers.map(printer => (
                      <SelectItem key={printer.id} value={column.name === 'printer_id' ? printer.id : printer.name}>
                        {printer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      }
      
      // Text areas for descriptions
      if (column.name.includes('description')) {
        return (
          <FormField
            key={column.name}
            control={form.control}
            name={column.name}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{column.name.replace(/_/g, ' ')}</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder={`Enter ${column.name.replace(/_/g, ' ')}`} 
                    {...field} 
                    value={field.value ? String(field.value) : ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      }
      
      // Default input fields for other columns
      return (
        <FormField
          key={column.name}
          control={form.control}
          name={column.name}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{column.name.replace(/_/g, ' ')}</FormLabel>
              <FormControl>
                <Input 
                  type={fieldType} 
                  placeholder={`Enter ${column.name.replace(/_/g, ' ')}`} 
                  {...field} 
                  value={field.value ? String(field.value) : ''}
                  onChange={e => {
                    const value = fieldType === 'number' 
                      ? e.target.value === '' ? '' : Number(e.target.value)
                      : e.target.value;
                    field.onChange(value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    });
  };
  
  return (
    <div className="container mx-auto py-8 max-w-7xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Component Manager</h1>
        <Button 
          variant="outline" 
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </div>
      
      <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
        <p className="text-sm text-yellow-800 dark:text-yellow-200">
          <strong>Temporary Mode:</strong> Component Manager is currently using mock data. 
          Changes will not be saved to the database until the new backend is integrated.
        </p>
      </div>
      
      {/* Component Type Selection */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Select Component Type</CardTitle>
          <CardDescription>Choose a component type to manage</CardDescription>
        </CardHeader>
        <CardContent>
          <Select onValueChange={handleComponentChange} value={selectedComponent?.id?.toString()}>
            <SelectTrigger className="w-full sm:w-[300px]">
              <SelectValue placeholder="Select component type" />
            </SelectTrigger>
            <SelectContent>
              {componentTypes.map(component => (
                <SelectItem key={component.id} value={component.id.toString()}>
                  {component.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {selectedComponent && (
            <p className="mt-2 text-sm text-muted-foreground">
              {selectedComponent.description || `Manage ${selectedComponent.name} components`}
            </p>
          )}
        </CardContent>
      </Card>
      
      {selectedComponent && (
        <>
          {/* Component Form */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>{isEditing ? `Edit ${selectedComponent.name}` : `Add New ${selectedComponent.name}`}</CardTitle>
              <CardDescription>
                {isEditing 
                  ? `Update information for this ${selectedComponent.name}` 
                  : `Fill in the details to add a new ${selectedComponent.name}`
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {renderFormFields()}
                  </div>
                  
                  <div className="flex justify-end gap-3">
                    {isEditing && (
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => {
                          setIsEditing(false);
                          setSelectedEntry(null);
                          form.reset({});
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button type="submit">
                      <Save className="mr-2 h-4 w-4" />
                      {isEditing ? 'Update' : 'Save'}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
          
          {/* Components List */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{selectedComponent.name} List</CardTitle>
                <Button variant="outline" size="sm" onClick={fetchEntries}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingEntries ? (
                <div className="flex justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : entries.length === 0 ? (
                <p className="text-center py-6 text-muted-foreground">
                  No {selectedComponent.name.toLowerCase()} entries found. Add one using the form above.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {tableColumns
                          .filter(col => !col.name.includes('description') && !col.name.includes('created_at'))
                          .map(column => (
                            <TableHead key={column.name}>{column.name.replace(/_/g, ' ')}</TableHead>
                          ))}
                        <TableHead className="w-[80px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.map(entry => (
                        <TableRow key={entry.id}>
                          {tableColumns
                            .filter(col => !col.name.includes('description') && !col.name.includes('created_at'))
                            .map(column => (
                              <TableCell key={`${entry.id}-${column.name}`}>
                                {column.type === 'boolean' 
                                  ? (entry[column.name] ? 'Yes' : 'No')
                                  : (entry[column.name] || '-')}
                              </TableCell>
                            ))}
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditEntry(entry)}>
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteEntry(entry)}
                                  className="text-destructive"
                                >
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ComponentManager;
